<template>
    <main class="main">
        <div class="container">
            <bread-crumbs default-url="reports" />
            <div class="row">
                <div class="col-12" v-if="generateReports">
                    <div class="row">
                        <div class="col-md-3" v-if="filters">
                            <the-filters
                                @applyFilters="applyFilters"
                                :filters="filters"
                                :module-name="reportsName"
                            />
                            <div class="reports-options">
                                <the-btn
                                    v-if="exportable"
                                    class="btn-block btn-primary mt-3"
                                    @click.native="$emit('onExport')"
                                    :disabled="
                                        !reports ||
                                            !reports.length ||
                                            allReportsAreInvalid
                                    "
                                    v-tooltip="exportableTooltip"
                                >
                                    <a-icon
                                        v-if="
                                            getReportsExportStatus !== 'loading'
                                        "
                                        icon="Upload"
                                        class="mr-1"
                                        height="20"
                                    />
                                    <button-spinner
                                        v-else
                                        class="mr-2"
                                        :status="getReportsExportStatus"
                                    />
                                    Eksportuj
                                </the-btn>
                            </div>
                        </div>
                        <div :class="[filters ? 'col-md-9' : 'col-md-12']">
                            <div class="c-settings" :class="{ 'pt-2': areSortOptions }">
                                <sub-section-header :title="reportsTitle" class="mr-3" />
                                <div
                                    class="c-settings-filters mt-2 mt-md-0"
                                    v-if="areSortOptions"
                                >
                                    <div class="d-block ml-3">
                                        <results-per-page
                                            v-if="pageSizeChangeable"
                                            :per-page="perPage"
                                            :text="'Pokazuj na stronie:'"
                                            :on-change="changeResultsPerPage"
                                        ></results-per-page>
                                    </div>
                                    <div class="d-block ml-3">
                                        <sort-component
                                            :text="'Sortuj po:'"
                                            :module-name="reportsName"
                                            :options="areSortOptions"
                                        ></sort-component>
                                    </div>
                                </div>
                            </div>
                            <selected-filters
                                v-if="filters"
                                class="mb-4"
                                :filters="filters"
                                :module-name="reportsName"
                                @applyFilters="applyFilters"
                            />
                            <div class="content">
                                <div
                                    v-if="
                                        conditionalDisplay &&
                                            conditionalDisplay.conditional
                                    "
                                    class="text-medium"
                                >
                                    {{ conditionalDisplay.text }}
                                </div>
                                <page-preloader
                                    v-else-if="reportsStatus === 'loading'"
                                ></page-preloader>

                                <template
                                    v-else-if="
                                        reports &&
                                            reports.length > 0 &&
                                            !allReportsAreInvalid
                                    "
                                >
                                    <template v-if="componentName">
                                        <div
                                            class="c-table reportsTable mb-4"
                                            :class="reportsName"
                                        >
                                            <slot />
                                            <div class="c-tbody">
                                                <component
                                                    :is="componentName"
                                                    v-for="(report,
                                                            idx) in reports"
                                                    :key="idx"
                                                    :data="report"
                                                ></component>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div
                                            class="c-table reportsTable mb-4"
                                            :class="reportsName"
                                            v-for="(report, idx) in reports"
                                            :key="idx"
                                        >
                                            <h4
                                                class="mb-3"
                                                v-if="report.title"
                                            >
                                                {{ report.title }}
                                            </h4>
                                            <div
                                                class="c-tbody"
                                                v-if="report.data.length"
                                            >
                                                <slot :name="'slot-' + idx" />
                                                <component
                                                    :is="report.component"
                                                    v-for="(rData,
                                                            idx) in report.data"
                                                    :key="idx"
                                                    :data="rData"
                                                ></component>
                                            </div>
                                            <div v-else>Brak wyników</div>
                                        </div>
                                    </template>
                                </template>
                                <empty-placeholder v-else-if="emptyPlaceholder">
                                    Raport {{ reportsTitle }} jest aktualnie niedostępny.<br />
                                    Wyniki Twojego sklepu możesz uzyskać u Doradcy Klienta,<br />
                                    zachęcamy do ich omawiania podczas wizyt.
                                </empty-placeholder>
                                <contact-placeholder v-else-if="contactPlaceholder" />
                                <div v-else v-text="resultsPhrase" />
                            </div>
                            <the-pagination
                                v-if="paging && paging.totalPages > 1"
                                :pagination="paging"
                                @change="onChange"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-12" v-else>
                    <page-preloader />
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import PromoRealization from '@/components/reports/PromoRealization'
import PromoBillings from '@/components/reports/PromoBillings'
import PromoUsage from '@/components/reports/PromoUsage'
import PromoPlan from '@/components/reports/PromoPlan'
import SaleGoals from '@/components/reports/SaleGoals'
import MKasaSaleItems from '@/components/reports/MKasaSaleItems'
import MKasaPromoItems from '@/components/reports/MKasaPromoItems'
import LotteryUsageSimple from '@/components/reports/LotteryUsageSimple'
import LotteryUsageAdvanced from '@/components/reports/LotteryUsageAdvanced'
import InvalidUsageProducts from '@/components/reports/InvalidUsageProducts'
import MyShop from '@/components/reports/MyShop'
import Targetowka from '@/components/reports/Targetowka'

export default {
    data() {
        return {
            generateReports: false,
            fetchFilters: true
        }
    },
    props: {
        reportsName: {
            type: String,
            required: true
        },
        componentName: {
            type: String,
            required: false
        },
        reportsTitle: {
            type: String,
            required: true
        },
        exportable: {
            type: Boolean,
            default: false
        },
        reportInvalidCondition: {
            type: Function,
            default: () => {}
        },
        pageSizeChangeable: {
            type: Boolean,
            default: false
        },
        conditionalDisplay: {
            type: Object,
            required: false
        },
        resultsPhrase: {
            type: String,
            default: 'Brak wyników'
        },
        exportableTooltip: {
            type: String,
            required: false
        },
        contactPlaceholder: {
            type: Boolean,
            default: false
        },
        emptyPlaceholder: {
            type: Boolean,
            default: false
        }
    },
    created() {
        this.fetch()
    },
    components: {
        PromoRealization,
        PromoBillings,
        PromoPlan,
        PromoUsage,
        SaleGoals,
        MKasaSaleItems,
        MKasaPromoItems,
        LotteryUsageSimple,
        LotteryUsageAdvanced,
        InvalidUsageProducts,
        MyShop,
        Targetowka
    },
    computed: {
        allReportsAreInvalid() {
            return this.reports.every(this.reportInvalidCondition)
        },
        reports() {
            return this.$store.getters['get' + this.reportsNameUpper]
        },
        filters() {
            return this.$store.getters[
                'get' + this.reportsNameUpper + 'Filters'
            ]
        },
        reportsStatus() {
            return this.$store.getters['get' + this.reportsNameUpper + 'Status']
        },
        paging() {
            const pagination = this.$store.getters[
                'get' + this.reportsNameUpper + 'Pagination'
            ]
            if (!pagination) {
                return null
            }

            const nextPage = pagination.page < pagination.totalPages
                ? pagination.page + 1
                : null
            const prevPage = pagination.page > 1
                ? pagination.page - 1
                : null

            return {
                ...pagination,
                nextPage,
                prevPage
            }
        },
        perPage() {
            return this.paging?.perPage
        },
        reportsNameUpper() {
            return (
                this.reportsName.charAt(0).toUpperCase() +
                this.reportsName.slice(1)
            )
        },
        getReportsExportStatus() {
            return this.$store.getters[
                `get${this.reportsNameUpper}ExportStatus`
            ]
        },
        areSortOptions() {
            return this.$store.getters['get' + this.reportsNameUpper + 'SortOptions']
        }
    },
    methods: {
        changeResultsPerPage(value) {
            this.$router.push({
                query: { ...this.$route.query, show: value, page: 1 }
            })
        },
        applyFilters() {
            this.fetch()
        },
        async fetch() {
            await this.$store.dispatch(`${this.reportsName}Fetch`)

            if (
                this.$store._actions[`${this.reportsName}FiltersFetch`] &&
                this.fetchFilters
            ) {
                await this.$store.dispatch(`${this.reportsName}FiltersFetch`)
                this.fetchFilters = false
            }
            this.generateReports = true
        },
        onChange(page) {
            this.$router.push({
                query: { ...this.$route.query, page }
            })
        }
    }
}
</script>

<style lang="scss">
@import '../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.reports-options {
    @include media-breakpoint-up(xl) {
        padding-right: 15px;
    }
}
</style>
